import React, { useState, useEffect, useRef, Suspense, useMemo, useCallback } from 'react';
import { generateColorScale, readableTextColor, contrastRatio } from '../utils/colorScale';
import { FEATURE_CONFIG } from '../utils/featureConfig';
import { useParams } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Heading,
  Spinner,
  Button,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useBreakpointValue,
} from '@chakra-ui/react';

const DesignStrategyVision = React.lazy(() => import('./DesignStrategyVision'));
const ProductStrategyView = React.lazy(() => import('./ProductStrategyView'));
const CompetitorAnalysisView = React.lazy(() => import('./CompetitorAnalysisView'));
const DecisionsView = React.lazy(() => import('./DecisionsView'));
const ReadmeGenerator = React.lazy(() => import('./ReadmeGenerator'));
const StepByStepView = React.lazy(() => import('./StepByStepView'));
const CalendarView = React.lazy(() => import('./CalendarView'));
const SlickView = React.lazy(() => import('./SlickView'));

// Kick off all chunk downloads immediately — so tabs feel instant on first click
const preloadViews = () => {
  import('./DesignStrategyVision');
  import('./ProductStrategyView');
  import('./CompetitorAnalysisView');
  import('./DecisionsView');
  import('./ReadmeGenerator');
  import('./StepByStepView');
  import('./CalendarView');
  import('./SlickView');
};


const ViewLoadingFallback = () => (
  <Flex justify="center" align="center" h="200px">
    <Spinner size="lg" color="brand.500" />
  </Flex>
);

const CHAKRA_BRAND_GRADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

const BRAND_CSS_VARS = [
  '--brand-page-bg', '--brand-block-bg', '--brand-border',
  '--brand-text-muted', '--brand-primary', '--brand-primary-hover',
  '--brand-heading', '--brand-secondary', '--brand-secondary-hover',
  '--brand-text', '--brand-text-on-page', '--brand-text-on-block',
  '--brand-text-on-primary', '--brand-text-on-secondary',
  '--brand-accent-card-bg', '--brand-text-on-accent-card',
  '--brand-text-on-block-muted', '--brand-block-bg-hover',
  '--brand-text-on-primary-500', '--brand-text-on-primary-600',
  '--brand-text-on-primary-700', '--brand-text-on-primary-800',
  '--brand-heading-font', '--brand-body-font',
  ...CHAKRA_BRAND_GRADES.map(g => `--chakra-colors-brand-${g}`),
  ...CHAKRA_BRAND_GRADES.map(g => `--chakra-colors-teal-${g}`),
  // Chakra gray overrides for dark brand themes
  '--chakra-colors-gray-50',  '--chakra-colors-gray-100',
  '--chakra-colors-gray-200', '--chakra-colors-gray-300',
  '--chakra-colors-gray-500', '--chakra-colors-gray-600',
  '--chakra-colors-gray-700', '--chakra-colors-gray-800',
  // Chakra semantic colour tints
  '--chakra-colors-green-50',  '--chakra-colors-green-100',
  '--chakra-colors-green-600', '--chakra-colors-green-700', '--chakra-colors-green-800',
  '--chakra-colors-orange-50', '--chakra-colors-orange-100',
  '--chakra-colors-orange-600','--chakra-colors-orange-700','--chakra-colors-orange-800',
  '--chakra-colors-blue-50',   '--chakra-colors-blue-100',
  '--chakra-colors-blue-600',  '--chakra-colors-blue-700',  '--chakra-colors-blue-800',
  '--chakra-colors-red-50',    '--chakra-colors-red-100',
  '--chakra-colors-red-600',   '--chakra-colors-red-700',   '--chakra-colors-red-800',
  '--chakra-colors-yellow-50', '--chakra-colors-yellow-100',
  '--chakra-colors-yellow-600','--chakra-colors-yellow-700','--chakra-colors-yellow-800',
  '--chakra-colors-purple-50', '--chakra-colors-purple-100',
  '--chakra-colors-purple-600','--chakra-colors-purple-700','--chakra-colors-purple-800',
  '--chakra-colors-teal-600',  '--chakra-colors-teal-700',  '--chakra-colors-teal-800',
];

const DARK_VARS = {
  '--brand-page-bg':           '#1A202C',
  '--brand-block-bg':          '#2D3748',
  '--brand-block-bg-hover':    'rgba(255,255,255,0.08)',
  '--brand-border':            'rgba(255,255,255,0.1)',
  '--brand-heading':           '#F7FAFC',
  '--brand-text':              '#F7FAFC',
  '--brand-text-on-page':      '#F7FAFC',
  '--brand-text-on-block':     '#F7FAFC',
  '--brand-text-muted':        '#A0AEC0',
  '--brand-text-on-block-muted': '#A0AEC0',
  '--brand-primary':           '#63B3ED',
  '--brand-primary-hover':     '#90CDF4',
  '--brand-secondary':         '#68D391',
  '--brand-secondary-hover':   '#9AE6B4',
  '--brand-accent-card-bg':    '#2D3748',
  '--brand-text-on-accent-card': '#F7FAFC',
  // Flip lighter Chakra grays so borders and subtle bgs adapt in dark mode
  '--chakra-colors-gray-50':   '#2D3748',
  '--chakra-colors-gray-100':  'rgba(255,255,255,0.07)',
  '--chakra-colors-gray-200':  'rgba(255,255,255,0.10)',
  '--chakra-colors-gray-300':  'rgba(255,255,255,0.14)',
  '--chakra-colors-gray-500':  '#A0AEC0',
  '--chakra-colors-gray-600':  '#CBD5E0',
  '--chakra-colors-gray-700':  '#E2E8F0',
  '--chakra-colors-gray-800':  '#F7FAFC',
  // Semantic colour tints — swap solid pastels for semi-transparent versions
  '--chakra-colors-green-50':  'rgba(72,187,120,0.12)',
  '--chakra-colors-green-100': 'rgba(72,187,120,0.20)',
  '--chakra-colors-green-600': '#68D391',
  '--chakra-colors-green-700': '#48BB78',
  '--chakra-colors-green-800': '#9AE6B4',
  '--chakra-colors-orange-50': 'rgba(237,137,54,0.12)',
  '--chakra-colors-orange-100':'rgba(237,137,54,0.20)',
  '--chakra-colors-orange-600': '#F6AD55',
  '--chakra-colors-orange-700': '#ED8936',
  '--chakra-colors-orange-800': '#FBD38D',
  '--chakra-colors-blue-50':   'rgba(99,179,237,0.12)',
  '--chakra-colors-blue-100':  'rgba(99,179,237,0.20)',
  '--chakra-colors-blue-600':  '#63B3ED',
  '--chakra-colors-blue-700':  '#90CDF4',
  '--chakra-colors-blue-800':  '#BEE3F8',
  '--chakra-colors-red-50':    'rgba(245,101,101,0.12)',
  '--chakra-colors-red-100':   'rgba(245,101,101,0.20)',
  '--chakra-colors-red-600':   '#FC8181',
  '--chakra-colors-red-700':   '#FEB2B2',
  '--chakra-colors-red-800':   '#FED7D7',
  '--chakra-colors-yellow-50': 'rgba(236,201,75,0.12)',
  '--chakra-colors-yellow-100':'rgba(236,201,75,0.20)',
  '--chakra-colors-yellow-600':'#F6E05E',
  '--chakra-colors-yellow-700':'#FAF089',
  '--chakra-colors-yellow-800':'#FEFCBF',
  '--chakra-colors-purple-50': 'rgba(159,122,234,0.12)',
  '--chakra-colors-purple-100':'rgba(159,122,234,0.20)',
  '--chakra-colors-purple-600':'#B794F4',
  '--chakra-colors-purple-700':'#D6BCFA',
  '--chakra-colors-purple-800':'#E9D8FD',
  '--chakra-colors-teal-600':  '#4FD1C5',
  '--chakra-colors-teal-700':  '#81E6D9',
  '--chakra-colors-teal-800':  '#B2F5EA',
};

const SharedPlanViewer = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [currentView, setCurrentView] = useState('strategy');
  const [isDark, setIsDark] = useState(() => localStorage.getItem('shared-theme') === 'dark');

  // Sliding pill nav
  const navContainerRef = useRef(null);
  const tabRefs = useRef({});
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0, opacity: 0 });

  // Overflow ("...") menu for mobile
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowBtnRef = useRef(null);
  const maxInline = useBreakpointValue({ base: 2, sm: 3, md: 99 }) ?? 99;

  // Track viewport width for responsive layout decisions
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isNarrowLayout = windowWidth < 900;

  useEffect(() => {
    const loadSharedPlan = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/plans/shared/${token}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('This share link is not valid or has been revoked.');
          } else if (response.status === 410) {
            throw new Error('This share link has expired.');
          }
          throw new Error('Failed to load shared plan.');
        }
        
        const data = await response.json();
        setPlanData(data);
        preloadViews();
        
        if (data.plan?.enabled_features) {
          const features = typeof data.plan.enabled_features === 'string'
            ? JSON.parse(data.plan.enabled_features)
            : data.plan.enabled_features;
          const firstEnabled = features.find(f => FEATURE_CONFIG[f]);
          if (firstEnabled) setCurrentView(FEATURE_CONFIG[firstEnabled].viewKey);
        }
      } catch (err) {
        console.error('Error fetching shared plan:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadSharedPlan();
  }, [token]);

  // Apply brand palette as CSS vars on mount, reset on unmount
  useEffect(() => {
    const brand = planData?.brand_settings;
    if (!brand?.primary_color) return;

    const root = document.documentElement;
    const p = generateColorScale(brand.primary_color);
    const s = brand.secondary_color ? generateColorScale(brand.secondary_color) : null;

    // Semantic values from GPT — use directly when available, fall back to neutral defaults
    // (page/card bg have no semantic relationship to primary colour, so don't derive from it)
    const pageBg    = brand.page_bg_color || '#F7FAFC';          // neutral gray-50
    const blockBg   = brand.card_bg_color || '#FFFFFF';          // plain white
    const headingClr = brand.heading_color || (
      readableTextColor(pageBg) === '#ffffff'
        ? (p?.[300] || '#e2e8f0')   // dark page → use a light primary shade
        : (p?.[700] || '#1A202C')   // light page → use a dark primary shade
    );
    const bodyText  = brand.text_color    || readableTextColor(pageBg);

    root.style.setProperty('--brand-page-bg',           pageBg);
    root.style.setProperty('--brand-block-bg',          blockBg);
    root.style.setProperty('--brand-heading',           headingClr);
    root.style.setProperty('--brand-text',              bodyText);

    if (p) {
      root.style.setProperty('--brand-border',            p[200]);
      root.style.setProperty('--brand-primary',           p[500]);
      root.style.setProperty('--brand-primary-hover',     p[600]);

      // Remap Chakra's brand color scale → client brand
      // Makes colorScheme="brand" and color="brand.X" use the client's palette throughout all inner views
      CHAKRA_BRAND_GRADES.forEach(g => {
        root.style.setProperty(`--chakra-colors-brand-${g}`, p[g]);
      });
    }
    if (s) {
      root.style.setProperty('--brand-secondary',         s[500]);
      root.style.setProperty('--brand-secondary-hover',   s[600]);
      root.style.setProperty('--brand-text-on-secondary', readableTextColor(s[500]));

      // Remap Chakra teal (used in StepByStepView tags) → client secondary
      CHAKRA_BRAND_GRADES.forEach(g => {
        root.style.setProperty(`--chakra-colors-teal-${g}`, s[g]);
      });
    }


    // Contrast-safe text for every brand surface
    const textOnPage  = readableTextColor(pageBg);
    const textOnBlock = readableTextColor(blockBg);
    root.style.setProperty('--brand-text-on-page',         textOnPage);
    root.style.setProperty('--brand-text-on-block',        textOnBlock);
    root.style.setProperty('--brand-text-on-primary',      p ? readableTextColor(p[500]) : '#ffffff');

    // Per-grade readable text — needed for branded bg panels that use specific shades (e.g. brand.700)
    if (p) {
      [500, 600, 700, 800].forEach(g => {
        root.style.setProperty(`--brand-text-on-primary-${g}`, readableTextColor(p[g]));
      });
    }

    // Neutral muted variants — neutral gray that works on each surface (not primary-derived)
    root.style.setProperty('--brand-text-muted',           textOnPage  === '#ffffff' ? '#A0AEC0' : '#718096');
    root.style.setProperty('--brand-text-on-block-muted',  textOnBlock === '#ffffff' ? '#A0AEC0' : '#718096');

    // When the brand page is dark, remap Chakra's gray tokens so that views using
    // color="gray.X" on brand surfaces don't produce dark text on dark backgrounds.
    // Only override on dark themes — light themes keep Chakra's default grays.
    if (textOnPage === '#ffffff') {
      // Flip dark grays to their light-scale equivalents so color="gray.X" stays
      // readable on dark brand surfaces without touching each view component.
      root.style.setProperty('--chakra-colors-gray-800', '#F7FAFC');
      root.style.setProperty('--chakra-colors-gray-700', '#E2E8F0');
      root.style.setProperty('--chakra-colors-gray-600', '#CBD5E0');
      root.style.setProperty('--chakra-colors-gray-500', '#A0AEC0');
    }

    // Accent card: use the actual scanned brand hex directly — not a computed scale grade.
    // Scale grades like s[700] or p[800] drift from the real brand colour (e.g. yellow → mustard).
    const accentCardBg = brand.secondary_color || brand.primary_color || '#2D3748';
    root.style.setProperty('--brand-accent-card-bg',      accentCardBg);
    root.style.setProperty('--brand-text-on-accent-card', readableTextColor(accentCardBg));

    if (brand.heading_font) root.style.setProperty('--brand-heading-font', `'${brand.heading_font}', serif`);
    if (brand.body_font)    root.style.setProperty('--brand-body-font',    `'${brand.body_font}', sans-serif`);

    // Load Google Fonts
    const fonts = [brand.heading_font, brand.body_font].filter(Boolean);
    if (fonts.length > 0) {
      const families = fonts.map(f => f.replace(/ /g, '+')).join('&family=');
      const link = document.createElement('link');
      link.id = 'shared-brand-fonts';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
      document.head.appendChild(link);
    }

    return () => {
      BRAND_CSS_VARS.forEach(v => root.style.removeProperty(v));
      document.getElementById('shared-brand-fonts')?.remove();
    };
  }, [planData?.brand_settings]);

  // Apply/remove dark mode CSS vars — only when there's no brand (brand defines its own palette)
  useEffect(() => {
    const brand = planData?.brand_settings;
    if (brand?.primary_color) return; // brand takes over, no dark toggle

    const root = document.documentElement;
    if (isDark) {
      Object.entries(DARK_VARS).forEach(([k, v]) => root.style.setProperty(k, v));
    } else {
      Object.keys(DARK_VARS).forEach(k => root.style.removeProperty(k));
    }
    return () => {
      Object.keys(DARK_VARS).forEach(k => root.style.removeProperty(k));
    };
  }, [isDark, planData?.brand_settings]);

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev;
      localStorage.setItem('shared-theme', next ? 'dark' : 'light');
      return next;
    });
  };

  const parseJSON = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const formatDuration = (hours) => {
    if (!hours || hours === 0) return "0 hours";

    if (hours < 8) {
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    } else if (hours < 40) {
      const days = Math.floor(hours / 8);
      const remainingHours = hours % 8;
      let result = `${days} day${days !== 1 ? "s" : ""}`;
      if (remainingHours > 0) {
        result += ` ${remainingHours} hour${remainingHours !== 1 ? "s" : ""}`;
      }
      return result;
    } else {
      const weeks = Math.floor(hours / 40);
      const remainingHours = hours % 40;
      const days = Math.floor(remainingHours / 8);
      const finalHours = remainingHours % 8;

      let result = `${weeks} week${weeks !== 1 ? "s" : ""}`;
      if (days > 0) {
        result += ` ${days} day${days !== 1 ? "s" : ""}`;
      }
      if (finalHours > 0) {
        result += ` ${finalHours} hour${finalHours !== 1 ? "s" : ""}`;
      }
      return result;
    }
  };

  const plan = planData?.plan;
  const enabledFeatures = parseJSON(plan?.enabled_features) || [];
  const finalProgramSequence = parseJSON(plan?.final_program_sequence) || [];

  const totalDuration = useMemo(() => {
    return finalProgramSequence.reduce((sum, program) => {
      return sum + (program.totalProgramLength || program.duration || 0);
    }, 0);
  }, [finalProgramSequence]);

  // When the backend has applied a visibility_config filter, enabled_features is already
  // the filtered list — skip the "extras" fallback so hidden sections can't sneak back in.
  const shareScopedFeatures = planData?.shareScopedFeatures ?? false;
  const visibleSubsections = planData?.visibleSubsections ?? null;

  const visibleTabs = useMemo(() => {
    if (!planData) return [];

    const hasData = (featureId) => {
      switch (featureId) {
        case 'pulse_view':
          return !!plan?.short_summary; // Pulse always available if plan exists
        case 'strategy_view':
          return !!plan?.short_summary;
        case 'step_by_step_view':
          return finalProgramSequence.length > 0;
        case 'calendar_view':
          return finalProgramSequence.length > 0;
        case 'slick_view':
          return finalProgramSequence.length > 0;
        case 'product_strategy_view':
          return !!planData?.productStrategy;
        case 'competitor_analysis_view':
          return !!planData?.competitorAnalysis;
        case 'decisions_view':
          return !!planData?.productStrategy || !!planData?.competitorAnalysis;
        case 'readme_view':
          return !!(planData?.prdContent?.latest || planData?.prdContent);
        default:
          return false;
      }
    };

    // Use enabledFeatures order (same as /plan page). Exclude Pulse — it's interactive-only.
    const ordered = enabledFeatures.filter(f => FEATURE_CONFIG[f] && f !== 'pulse_view');
    // Only add extras (features with data not in enabledFeatures) when NOT share-scoped.
    // When shareScopedFeatures is true the backend already returned the filtered list —
    // adding extras here would bypass the owner's visibility settings.
    const extras = shareScopedFeatures
      ? []
      : Object.keys(FEATURE_CONFIG).filter(f => !enabledFeatures.includes(f) && hasData(f));

    return [...ordered, ...extras].map(featureId => {
      const config = FEATURE_CONFIG[featureId];
      return {
        id: config.viewKey,
        label: config.label,
        icon: config.icon,
        feature: featureId,
      };
    });
  }, [enabledFeatures, plan, planData, finalProgramSequence, shareScopedFeatures]);

  // Split tabs: always keep the active tab visible in the inline row
  const { inlineTabs, overflowTabs } = useMemo(() => {
    if (visibleTabs.length <= maxInline) {
      return { inlineTabs: visibleTabs, overflowTabs: [] };
    }
    // Reserve last slot for the "..." button
    const slots = maxInline - 1;
    let inline = visibleTabs.slice(0, slots);
    let overflow = visibleTabs.slice(slots);

    // If the active tab ended up in overflow, swap it with the last inline tab
    const activeOverflow = overflow.find(t => t.id === currentView);
    if (activeOverflow) {
      const bumped = inline[inline.length - 1];
      inline = [...inline.slice(0, -1), activeOverflow];
      overflow = [bumped, ...overflow.filter(t => t.id !== currentView)];
    }
    return { inlineTabs: inline, overflowTabs: overflow };
  }, [visibleTabs, maxInline, currentView]);

  // Close overflow menu when clicking outside
  useEffect(() => {
    if (!overflowOpen) return;
    const handler = (e) => {
      if (overflowBtnRef.current && !overflowBtnRef.current.contains(e.target)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowOpen]);

  // Measure active tab position and slide the pill — must be after visibleTabs
  useEffect(() => {
    const container = navContainerRef.current;
    const activeEl  = tabRefs.current[currentView];
    if (!container || !activeEl) return;
    const cRect = container.getBoundingClientRect();
    const aRect = activeEl.getBoundingClientRect();
    setPillStyle({ left: aRect.left - cRect.left, width: aRect.width, opacity: 1 });
  }, [currentView, visibleTabs, maxInline]);

  if (loading) {
    return (
      <Flex h="100vh" justify="center" align="center" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.600">Loading shared plan...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex h="100vh" justify="center" align="center" bg="gray.50" p={4}>
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          borderRadius="lg"
          maxW="500px"
          p={8}
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Unable to Access Plan
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            {error}
          </AlertDescription>
          <Button
            mt={6}
            colorScheme="brand"
            onClick={() => window.location.href = '/'}
          >
            Go to Homepage
          </Button>
        </Alert>
      </Flex>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'pulse':
        return (
          <Box p={8} textAlign="center">
            <Box as="span" className="material-icons-outlined" fontSize="48px" color="brand.300" mb={4} display="block">
              monitor_heart
            </Box>
            <Text fontWeight="semibold" color="gray.700" mb={2}>Pulse</Text>
            <Text fontSize="sm" color="gray.500">
              Pulse check-ins are live and interactive — they're not available in view-only mode.
            </Text>
          </Box>
        );
      case 'strategy':
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <DesignStrategyVision
              shortSummary={plan?.short_summary}
              problemStatement={plan?.problem_statement}
              goalStatement={plan?.goal_statement}
              targetStatement={plan?.target_statement}
              objectivesStatement={parseJSON(plan?.objectives_statement)}
              setIsSaved={() => {}}
              isReadOnly={true}
              visibleSubsections={visibleSubsections?.strategy_view ?? null}
              contentPanelWidth={windowWidth}
            />
          </Suspense>
        );
      
      case 'stepByStep':
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <StepByStepView
              contentPanelWidth={windowWidth}
              finalProgramSequence={finalProgramSequence}
              setFinalProgramSequence={() => {}}
              setSelectedPrograms={() => {}}
              setTotalDuration={() => {}}
              setIsSaved={() => {}}
              handleRemoveProgram={() => {}}
              formatDuration={formatDuration}
              handleAddToolClick={() => {}}
              handleDurationChange={() => {}}
              handleRemoveTool={() => {}}
              handleProgramFieldChange={() => {}}
              handleGenerateField={() => {}}
              loadingState={{}}
              handleAddProgramClick={() => {}}
              totalDuration={totalDuration}
              handleComponentAction={() => {}}
              currentDesignChallenge={plan?.short_summary}
              shortSummary={plan?.short_summary}
              goalStatement={plan?.goal_statement}
              problemStatement={plan?.problem_statement}
              targetStatement={plan?.target_statement}
              objectivesStatement={parseJSON(plan?.objectives_statement)}
              deliverablesStatement={parseJSON(plan?.deliverables_statement)}
              metricsStatement={parseJSON(plan?.metrics_statement)}
              stakeholdersCustomersInvolvement={parseJSON(plan?.stakeholders_customers_involvement)}
              selectedPrograms={[]}
              currentPlanId={plan?.id}
              isReadOnly={true}
            />
          </Suspense>
        );
      
      case 'calendar':
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <CalendarView
              contentPanelWidth={windowWidth}
              programs={finalProgramSequence}
              finalProgramSequence={finalProgramSequence}
              totalDuration={totalDuration}
              shortSummary={plan?.short_summary}
              goalStatement={plan?.goal_statement}
              problemStatement={plan?.problem_statement}
              targetStatement={plan?.target_statement}
              objectivesStatement={parseJSON(plan?.objectives_statement)}
              onStartOptionChange={() => {}}
              setIsSaved={() => {}}
              handleComponentAction={() => {}}
              currentPlanId={plan?.id}
              programsOfWork={[]}
              methods={[]}
              tools={[]}
              deliverables={[]}
              selectedPrograms={[]}
            />
          </Suspense>
        );

      case 'slick':
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <SlickView
              contentPanelWidth={windowWidth}
              programs={finalProgramSequence}
              finalProgramSequence={finalProgramSequence}
              totalDuration={totalDuration}
              shortSummary={plan?.short_summary}
              goalStatement={plan?.goal_statement}
              problemStatement={plan?.problem_statement}
              targetStatement={plan?.target_statement}
              objectivesStatement={parseJSON(plan?.objectives_statement)}
              handleComponentAction={() => {}}
              currentPlanId={plan?.id}
              programsOfWork={[]}
              methods={[]}
              tools={[]}
              deliverables={[]}
              selectedPrograms={[]}
            />
          </Suspense>
        );

      case 'productStrategy':
        if (!planData?.productStrategy) {
          return (
            <Box p={8}>
              <Text color="gray.500">Product strategy not available for this plan.</Text>
            </Box>
          );
        }
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <ProductStrategyView
              currentPlanId={plan?.id}
              shortSummary={plan?.short_summary}
              problemStatement={plan?.problem_statement}
              goalStatement={plan?.goal_statement}
              targetStatement={plan?.target_statement}
              isReadOnly={true}
              initialReportData={planData.productStrategy}
              visibleSubsections={visibleSubsections?.product_strategy_view ?? null}
              isNarrowLayout={isNarrowLayout}
              contentPanelWidth={windowWidth}
            />
          </Suspense>
        );
      
      case 'competitor':
        if (!planData?.competitorAnalysis) {
          return (
            <Box p={8}>
              <Text color="gray.500">Competitor analysis not available for this plan.</Text>
            </Box>
          );
        }
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <CompetitorAnalysisView
              currentPlanId={plan?.id}
              shortSummary={plan?.short_summary}
              isReadOnly={true}
              initialReportData={planData.competitorAnalysis}
              visibleSubsections={visibleSubsections?.competitor_analysis_view ?? null}
              contentPanelWidth={windowWidth}
            />
          </Suspense>
        );
      
      case 'decisions':
        if (!planData?.productStrategy && !planData?.competitorAnalysis) {
          return (
            <Box p={8}>
              <Text color="gray.500">No strategic decisions available for this plan yet.</Text>
            </Box>
          );
        }
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <DecisionsView
              currentPlanId={plan?.id}
              isReadOnly={true}
              initialProductStrategyData={planData.productStrategy}
              initialCompetitorData={planData.competitorAnalysis}
              visibleSubsections={visibleSubsections?.decisions_view ?? null}
              isNarrowLayout={isNarrowLayout}
              contentPanelWidth={windowWidth}
            />
          </Suspense>
        );
      
      case 'readme':
        if (!planData?.prdContent?.latest && !planData?.prdContent) {
          return (
            <Box p={8}>
              <Text color="gray.500">Documents not available for this plan.</Text>
            </Box>
          );
        }
        return (
          <Suspense fallback={<ViewLoadingFallback />}>
            <ReadmeGenerator
              currentPlanId={plan?.id}
              planName={plan?.plan_name}
              shortSummary={plan?.short_summary}
              isReadOnly={true}
              initialContent={planData.prdContent}
              contentPanelWidth={windowWidth}
            />
          </Suspense>
        );
      
      default:
        return (
          <Box p={8}>
            <Text color="gray.500">Select a tab to view content.</Text>
          </Box>
        );
    }
  };

  const brand = planData?.brand_settings;

  const headerBg     = 'var(--brand-page-bg, #F7FAFC)';
  const headerBorder = 'var(--brand-border, #E2E8F0)';
  const headingColor = 'var(--brand-heading, #1A202C)';
  const mutedColor   = 'var(--brand-text-on-page, #718096)';
  const bodyFont     = brand?.body_font ? `var(--brand-body-font, inherit)` : undefined;
  const headingFont  = brand?.heading_font ? `var(--brand-heading-font, inherit)` : undefined;

  // Floating nav colours — frosted glass adapts to page bg luminance
  const pageBgForNav = brand?.page_bg_color || '#F7FAFC';
  const isPageDark   = brand ? readableTextColor(pageBgForNav) === '#ffffff' : false;
  const navBg        = isPageDark ? 'rgba(15,15,15,0.84)'             : 'rgba(255,255,255,0.88)';
  const navBorder    = isPageDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)';
  const navMutedClr  = isPageDark ? '#ffffff'                           : '#1a1a1a';

  // Accessible active-button — walk scale until WCAG AA (4.5:1) is met.
  // Grades go to 900 so even very light primaries (pastel, yellow) find a dark
  // enough shade. readableTextColor bias can still return white at ~3:1 on mid
  // luminance colours, so we check the actual ratio after the pick.
  const navActiveBg = (() => {
    if (!brand?.primary_color) return { hex: '#3182CE', textColor: '#ffffff' };
    const p = generateColorScale(brand.primary_color);
    for (const grade of [500, 600, 700, 800, 900]) {
      const bg = p[grade];
      const textColor = readableTextColor(bg);
      if (contrastRatio(bg, textColor) >= 4.5) return { hex: bg, textColor };
    }
    // Absolute fallback — use black bg with white text
    return { hex: '#1A202C', textColor: '#ffffff' };
  })();

  // Parse hex → r,g,b so we can build rgba() strings for smooth CSS transitions.
  // transparent → hex causes a black flash; rgba(r,g,b,0) → rgba(r,g,b,1) does not.
  const parseRgb = (hex) => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    return [parseInt(full.slice(0,2),16), parseInt(full.slice(2,4),16), parseInt(full.slice(4,6),16)];
  };
  const [nr, ng, nb] = navActiveBg.hex.startsWith('#') ? parseRgb(navActiveBg.hex) : [49,130,206];

  return (
    <Box
      minH="100vh"
      bg="var(--brand-page-bg, #F7FAFC)"
      fontFamily={bodyFont}
      sx={{
        '@keyframes viewFadeIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to:   { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Header — no bg, no divider, flows with page */}
      <Box pt={4} pb={2} pl={{ base: 4, md: 32 }} pr={{ base: 4, md: 32 }} maxW="1400px" mx="auto">
          {/* Top row: logo+company left, badges+home right */}
          <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
            <HStack spacing={2}>
              {brand?.logo_url ? (
                <Box
                  as="img"
                  src={brand.logo_url}
                  alt={brand.company_name || 'Logo'}
                  maxH="28px"
                  maxW="100px"
                  objectFit="contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Box
                  as="span"
                  className="material-icons-outlined"
                  fontSize="22px"
                  color={brand ? 'var(--brand-primary, #3182CE)' : 'brand.500'}
                >
                  description
                </Box>
              )}
              {brand?.company_name && (
                <Text fontSize="sm" fontWeight="500" color={mutedColor} fontFamily={bodyFont}>
                  {brand.company_name}
                </Text>
              )}
            </HStack>

            <HStack spacing={3}>
              <Badge colorScheme="blue" variant="subtle" fontSize="2xs">
                <HStack spacing={1}>
                  <Box as="span" className="material-icons-outlined" fontSize="10px">visibility</Box>
                  <Text>View Only</Text>
                </HStack>
              </Badge>
              {planData?.viewCount && (
                <Text fontSize="2xs" color={mutedColor} opacity={0.7}>
                  {planData.viewCount} views
                </Text>
              )}
              {!brand?.primary_color && (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  onClick={toggleTheme}
                  style={{ color: isDark ? '#F7FAFC' : '#1A202C', opacity: 0.7 }}
                >
                  <Box as="span" className="material-icons-outlined" fontSize="20px">
                    {isDark ? 'light_mode' : 'dark_mode'}
                  </Box>
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                aria-label="Go to PABS"
                style={brand ? { color: 'var(--brand-text-on-page)', opacity: 0.6 } : { color: isDark ? '#F7FAFC' : '#1A202C', opacity: 0.6 }}
                onClick={() => window.location.href = '/'}
              >
                <Box as="span" className="material-icons-outlined" fontSize="20px">home</Box>
              </Button>
            </HStack>
          </Flex>

          {/* Plan title — 60% of content width on desktop, full width on mobile */}
          <Box maxW={{ base: '100%', md: '60%' }}>
            <Heading
              color={headingColor}
              fontFamily={headingFont || "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif"}
              style={{
                fontSize: 'clamp(1rem, 5.5vw, 3.8rem)',
                fontWeight: 700,
                lineHeight: 1.05,
                letterSpacing: '0.01em',
                textTransform: 'uppercase',
                wordBreak: 'normal',
                overflowWrap: 'normal',
                hyphens: 'none',
              }}
            >
              {plan?.plan_name || plan?.short_summary || 'Shared Plan'}
            </Heading>
          </Box>
      </Box>

      {/* Content — no card bg, sits on the page bg */}
      <Box
        maxW="1400px"
        mx="auto"
        mt={{ base: -4, md: -12 }}
        minH="calc(100vh - 72px)"
        fontFamily={bodyFont}
        pb="110px"
        sx={brand ? {
          'h1, h2, h3, h4, h5, h6': {
            fontFamily: 'var(--brand-heading-font, inherit)',
            color: 'var(--brand-heading)',
          },
        } : undefined}
      >
        <Box key={currentView} sx={{ animation: 'viewFadeIn 0.22s ease' }}>
          {renderCurrentView()}
        </Box>
      </Box>

      {/* Floating bottom nav — responsive sliding pill */}
      {visibleTabs.length > 1 && (
        <Box
          position="fixed"
          bottom={6}
          left="50%"
          zIndex={100}
          borderRadius="2xl"
          px={2}
          py={2}
          style={{
            transform: 'translateX(-50%)',
            background: navBg,
            border: navBorder,
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          }}
        >
          {/* Relative container so pill + overflow menu are positioned against it */}
          <Box position="relative" ref={navContainerRef}>

            {/* Sliding pill */}
            <Box
              position="absolute"
              top="2px"
              bottom="2px"
              borderRadius="xl"
              pointerEvents="none"
              style={{
                left: pillStyle.left,
                width: pillStyle.width,
                opacity: pillStyle.opacity,
                background: `rgba(${nr},${ng},${nb},1)`,
                transition: pillStyle.opacity === 0
                  ? 'opacity 0.15s ease'
                  : 'left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.15s ease',
              }}
            />

            <HStack spacing={1} position="relative" zIndex={1}>
              {/* Inline tabs */}
              {inlineTabs.map(tab => {
                const isActive = currentView === tab.id;
                return (
                  <HStack
                    key={tab.id}
                    ref={(el) => { tabRefs.current[tab.id] = el; }}
                    as="button"
                    spacing={2}
                    onClick={() => setCurrentView(tab.id)}
                    px={4}
                    py={2}
                    borderRadius="xl"
                    cursor="pointer"
                    border="none"
                    outline="none"
                    style={{
                      background: 'transparent',
                      color: isActive ? navActiveBg.textColor : navMutedClr,
                      transition: 'color 0.25s ease',
                    }}
                  >
                    <Box as="span" className="material-icons-outlined" fontSize="18px" lineHeight="1">
                      {tab.icon}
                    </Box>
                    <Text fontSize="13px" fontWeight="medium" whiteSpace="nowrap" lineHeight="1">
                      {tab.label}
                    </Text>
                  </HStack>
                );
              })}

              {/* "..." overflow button — only shown when tabs don't all fit */}
              {overflowTabs.length > 0 && (
                <Box position="relative" ref={overflowBtnRef}>
                  {/* Overflow dropdown — renders above the nav */}
                  {overflowOpen && (
                    <Box
                      position="absolute"
                      bottom="calc(100% + 10px)"
                      right={0}
                      borderRadius="xl"
                      overflow="hidden"
                      style={{
                        background: navBg,
                        border: navBorder,
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        minWidth: '160px',
                        animation: 'viewFadeIn 0.15s ease',
                      }}
                    >
                      <VStack spacing={0} align="stretch" py={1}>
                        {overflowTabs.map(tab => {
                          const isActive = currentView === tab.id;
                          return (
                            <HStack
                              key={tab.id}
                              as="button"
                              spacing={3}
                              onClick={() => { setCurrentView(tab.id); setOverflowOpen(false); }}
                              px={4}
                              py={3}
                              cursor="pointer"
                              border="none"
                              outline="none"
                              w="100%"
                              style={{
                                background: isActive ? `rgba(${nr},${ng},${nb},0.12)` : 'transparent',
                                color: isActive ? `rgba(${nr},${ng},${nb},1)` : navMutedClr,
                                transition: 'background 0.15s ease, color 0.15s ease',
                              }}
                            >
                              <Box as="span" className="material-icons-outlined" fontSize="18px" lineHeight="1">
                                {tab.icon}
                              </Box>
                              <Text fontSize="13px" fontWeight={isActive ? 'semibold' : 'medium'} whiteSpace="nowrap" lineHeight="1">
                                {tab.label}
                              </Text>
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {/* The dots button itself */}
                  <HStack
                    as="button"
                    spacing={1}
                    onClick={() => setOverflowOpen(o => !o)}
                    px={3}
                    py={2}
                    borderRadius="xl"
                    cursor="pointer"
                    border="none"
                    outline="none"
                    style={{
                      background: overflowOpen ? `rgba(${nr},${ng},${nb},0.12)` : 'transparent',
                      color: navMutedClr,
                      transition: 'background 0.2s ease, color 0.2s ease',
                    }}
                  >
                    <Box as="span" className="material-icons-outlined" fontSize="20px" lineHeight="1">
                      more_horiz
                    </Box>
                  </HStack>
                </Box>
              )}
            </HStack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SharedPlanViewer;
